import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { useSignalR } from "src/hooks";
import { EditTaskItemFocusDto, FetchError, queryKeys, TaskItemDetailDto, useAccessToken } from "src/http";
import { optimisticallyUpdateQueryData } from "../utilities";

type Variables = {
  taskItemGuid: string;
  isFocused: boolean;
  ownerGuid: string;
};

const useEditTaskItemFocusRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editTaskItemFocusRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditTaskItemFocusDto = {
      isFocused: variables.isFocused,
    };

    const response = await fetch(`${configuration.apiRootUrl}/task-items/${variables.taskItemGuid}/focus`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editTaskItemFocusRequest };
};

const useEditTaskItemFocus = () => {
  const { editTaskItemFocusRequest } = useEditTaskItemFocusRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editTaskItemFocusRequest,
    onMutate: async (variables) => {
      let cachedDetailData: TaskItemDetailDto | undefined;

      // Update the action item drawer to update state of the "Add to Tasks in Focus" button
      cachedDetailData = await optimisticallyUpdateQueryData<TaskItemDetailDto>(
        queryClient,
        queryKeys.taskItems.getTaskItemDetail(variables.taskItemGuid),
        (cachedData) => ({
          ...cachedData,
          owners: cachedData.owners.map((owner) => {
            if (owner.user.userId === variables.ownerGuid) {
              return { ...owner, isFocused: variables.isFocused };
            }
            return owner;
          }),
        })
      );

      return { cachedDetailData };
    },
    onSuccess: (_data, variables) => {
      signalR.sendRefetchTaskItem(variables.taskItemGuid);
      signalR.sendRefetchUserTaskItems();
      signalR.sendRefetchTeamTaskItems();
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(
        queryKeys.taskItems.getTaskItemDetail(variables.taskItemGuid),
        context?.cachedDetailData
      );
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.taskItems.getTaskItemDetail(variables.taskItemGuid));
      queryClient.invalidateQueries(queryKeys.taskItems.allTeamTaskItems);
      queryClient.invalidateQueries(queryKeys.taskItems.allTeamTaskItemSubtasks);
      queryClient.invalidateQueries(queryKeys.taskItems.allUserTaskItems);
    },
  });
};

export { useEditTaskItemFocus };
